import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface OffroadEmissionsChartProps {
  location: string;
}

// Color palette for categories
const CATEGORY_COLORS: Record<string, { border: string; background: string }> = {
  'Agricultural': { border: '#8b5cf6', background: 'rgba(139, 92, 246, 0.6)' },
  'Airport Ground Support': { border: '#ec4899', background: 'rgba(236, 72, 153, 0.6)' },
  'Cargo Handling Equipment': { border: '#f59e0b', background: 'rgba(245, 158, 11, 0.6)' },
  'Commercial Harbor Craft': { border: '#10b981', background: 'rgba(16, 185, 129, 0.6)' },
  'Construction and Mining': { border: '#3b82f6', background: 'rgba(59, 130, 246, 0.6)' },
  'Forestry': { border: '#059669', background: 'rgba(5, 150, 105, 0.6)' },
  'Industrial': { border: '#ef4444', background: 'rgba(239, 68, 68, 0.6)' },
  'Lawn and Garden': { border: '#14b8a6', background: 'rgba(20, 184, 166, 0.6)' },
  'Light Commercial': { border: '#6366f1', background: 'rgba(99, 102, 241, 0.6)' },
  'Locomotive': { border: '#f97316', background: 'rgba(249, 115, 22, 0.6)' },
  'Military Tactical Support': { border: '#64748b', background: 'rgba(100, 116, 139, 0.6)' },
  'Ocean Going Vessels': { border: '#06b6d4', background: 'rgba(6, 182, 212, 0.6)' },
  'Oil Drilling': { border: '#84cc16', background: 'rgba(132, 204, 22, 0.6)' },
  'Pleasure Craft': { border: '#a855f7', background: 'rgba(168, 85, 247, 0.6)' },
  'Portable Equipment': { border: '#eab308', background: 'rgba(234, 179, 8, 0.6)' },
  'Recreational': { border: '#22c55e', background: 'rgba(34, 197, 94, 0.6)' },
  'Transport Refrigeration Unit': { border: '#0ea5e9', background: 'rgba(14, 165, 233, 0.6)' },
};

const OffroadEmissionsChart: React.FC<OffroadEmissionsChartProps> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  console.log('OffroadEmissionsChart: Component rendered with location:', location);

  const buildChart = useCallback(async () => {
    console.log('OffroadEmissionsChart: buildChart called', { location, hasCanvas: !!canvasRef.current, isCreating: creatingRef.current });
    
    if (!canvasRef.current) {
      console.warn('OffroadEmissionsChart: No canvas ref available');
      return;
    }
    if (creatingRef.current) {
      console.warn('OffroadEmissionsChart: Chart creation already in progress');
      return;
    }
    
    if (!location || location === 'Selected Location') {
      console.warn('OffroadEmissionsChart: Invalid location:', location);
      setError('Please select a location');
      setLoading(false);
      return;
    }
    
    creatingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      if (chartRef.current) { 
        chartRef.current.destroy(); 
        chartRef.current = null; 
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch (err) {
      console.warn('OffroadEmissionsChart: Error destroying existing chart:', err);
    }

    let ctx: CanvasRenderingContext2D | null;
    try {
      ctx = canvasRef.current.getContext('2d');
      if (!ctx) {
        creatingRef.current = false;
        setLoading(false);
        return;
      }

    await supabaseLocationService.initialize();
    const resolvedGeofips = await supabaseLocationService.getGeofips(location);
    
    if (!resolvedGeofips) {
      console.warn('OffroadEmissionsChart: No geofips for location:', location);
      setError('Location not found');
      creatingRef.current = false;
      setLoading(false);
      return;
    }

    // Get location info to determine geography type
    const { data: locationData } = await supabaseClient
      .from('population')
      .select('geography_type, county')
      .eq('geofips', resolvedGeofips)
      .limit(1);

    let queryGeofips = resolvedGeofips;
    // Map population table geography_type to offroad_emissions geography_type
    // Population table uses 'City' but offroad_emissions uses 'place'
    const popGeographyType = locationData?.[0]?.geography_type || 'place';
    let geographyType = popGeographyType === 'City' ? 'place' : popGeographyType.toLowerCase();
    
    // Convert geofips to string (no leading zeros - table stores as TEXT without leading zeros)
    const geofipsStrPlace = String(queryGeofips);
    
    // Check if place data exists for this location
    const { data: placeCheck } = await supabaseClient
      .from('offroad_emissions')
      .select('geofips')
      .eq('geofips', geofipsStrPlace)
      .eq('geography_type', 'place')
      .limit(1);
    
    // If no place data and it's a city, try county
    if ((!placeCheck || placeCheck.length === 0) && popGeographyType === 'City' && locationData?.[0]?.county) {
      const countyName = locationData[0].county;
      const { data: countyData } = await supabaseClient
        .from('population')
        .select('geofips')
        .eq('location_name', countyName)
        .eq('geography_type', 'County')
        .limit(1);
      
      if (countyData && countyData.length > 0) {
        queryGeofips = countyData[0].geofips;
        geographyType = 'county';
      }
    }

    // Final geofips string (no leading zeros)
    const geofipsStr = String(queryGeofips);
    
    console.log('OffroadEmissionsChart: Query params:', {
      location,
      resolvedGeofips,
      queryGeofips,
      geofipsStr,
      geographyType,
      popGeographyType,
      placeDataExists: placeCheck && placeCheck.length > 0
    });

    // Fetch offroad emissions data
    // Note: Table uses geofips (lowercase) as TEXT without leading zeros
    // Sort by category, year to match CSV file order (geofips is already filtered)
    const { data: offroadData, error: offroadError } = await supabaseClient
      .from('offroad_emissions')
      .select('year, category, ghg_emissions_tco2e')
      .eq('geofips', geofipsStr)
      .eq('geography_type', geographyType)
      .order('category', { ascending: true })
      .order('year', { ascending: true });

    if (offroadError) {
      console.error('OffroadEmissionsChart: Error fetching data:', offroadError);
      setError('Failed to load offroad emissions data');
      creatingRef.current = false;
      setLoading(false);
      return;
    }

    console.log('OffroadEmissionsChart: Fetched data:', {
      rowCount: offroadData?.length || 0,
      sampleRows: offroadData?.slice(0, 3),
      uniqueYears: offroadData ? Array.from(new Set(offroadData.map((r: any) => r.year))).sort() : [],
      uniqueCategories: offroadData ? Array.from(new Set(offroadData.map((r: any) => r.category))) : [],
      nonZeroCount: offroadData ? offroadData.filter((r: any) => parseFloat(r.ghg_emissions_tco2e) > 0).length : 0
    });

    if (!offroadData || offroadData.length === 0) {
      console.warn('OffroadEmissionsChart: No data found for location:', location, {
        geofipsStr,
        geographyType,
        queryGeofips,
        resolvedGeofips,
        placeCheck: placeCheck?.length || 0
      });
      setError('No offroad emissions data available');
      creatingRef.current = false;
      setLoading(false);
      return;
    }

    // Check if we have any non-zero emissions
    const hasNonZeroEmissions = offroadData.some((r: any) => parseFloat(r.ghg_emissions_tco2e) > 0);
    if (!hasNonZeroEmissions) {
      console.warn('OffroadEmissionsChart: All emissions are zero for location:', location);
      // Still render the chart, but it will show zeros
    }

    // Organize data by year and category
    const dataByYear: Record<number, Record<string, number>> = {};
    const allYears = new Set<number>();
    const allCategories = new Set<string>();

    offroadData.forEach((row: any) => {
      const year = parseInt(row.year);
      const category = row.category;
      const emissions = parseFloat(row.ghg_emissions_tco2e) || 0;

      allYears.add(year);
      allCategories.add(category);

      if (!dataByYear[year]) {
        dataByYear[year] = {};
      }
      dataByYear[year][category] = emissions;
    });

    // Sort years and categories, filter years to 2000-2023
    const sortedYears = Array.from(allYears)
      .filter(year => year >= 2000 && year <= 2023)
      .sort((a, b) => a - b);
    const sortedCategories = Array.from(allCategories).sort();

    // Create datasets for each category
    const datasets = sortedCategories.map(category => {
      const color = CATEGORY_COLORS[category] || { 
        border: '#6b7280', 
        background: 'rgba(107, 114, 128, 0.6)' 
      };

      return {
        label: category,
        data: sortedYears.map(year => dataByYear[year]?.[category] || 0),
        borderColor: color.border,
        backgroundColor: color.background,
        borderWidth: 0, // No border for stacked area chart
        fill: true,
        tension: 0,
        pointRadius: 0,
        stack: 'stack1', // Stack all datasets
      };
    });

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: sortedYears,
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { 
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 10,
              font: {
                size: 11
              },
              boxWidth: 12,
            }
          },
          title: { 
            display: true, 
            text: `Offroad Emissions by Category (${location})`, 
            font: { size: 16, weight: 'bold' } 
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              // Return empty string for label - we'll use afterBody to show all items in correct order
              label: function() {
                return '';
              },
              // Show all items in reverse order (top to bottom, matching visual stack)
              afterBody: function(tooltipItems: any[]) {
                // Reverse the items to show top-to-bottom order (matching visual stack from top)
                // Filter out items with zero values for cleaner display
                const nonZeroItems = tooltipItems.filter((item: any) => (item.parsed.y || 0) > 0);
                const reversed = [...nonZeroItems].reverse();
                return reversed.map((item: any) => {
                  const label = item.dataset.label || '';
                  const value = item.parsed.y || 0;
                  return `${label}: ${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} tCO₂e`;
                });
              },
              footer: function(tooltipItems: any[]) {
                const total = tooltipItems.reduce((sum, item) => sum + (item.parsed.y || 0), 0);
                return `Total: ${total.toLocaleString(undefined, { maximumFractionDigits: 1 })} tCO₂e`;
              }
            }
          }
        },
        scales: {
          y: { 
            beginAtZero: true, 
            position: 'left',
            title: { display: true, text: 'Emissions (tCO₂e)' },
            stacked: true,
          },
          x: { 
            title: { display: true, text: 'Year' },
            stacked: true,
          },
        },
        interaction: { 
          mode: 'index', 
          intersect: false 
        },
      },
    });

      creatingRef.current = false;
      setLoading(false);
    } catch (err: any) {
      console.error('OffroadEmissionsChart: Error building chart:', err);
      setError(`Failed to load chart: ${err?.message || 'Unknown error'}`);
      creatingRef.current = false;
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    console.log('OffroadEmissionsChart: useEffect triggered', { location, hasCanvas: !!canvasRef.current });
    
    if (!location || location === 'Selected Location') {
      console.warn('OffroadEmissionsChart: Invalid location, skipping chart build');
      setError('Please select a location');
      setLoading(false);
      return;
    }

    // Wait for canvas to be available
    if (!canvasRef.current) {
      console.log('OffroadEmissionsChart: Canvas not available yet, waiting...');
      // Use a small delay to allow React to render the canvas
      const canvasNode = canvasRef.current;
      const timeoutId = setTimeout(() => {
        if (canvasRef.current) {
          console.log('OffroadEmissionsChart: Canvas now available, building chart');
          buildChart().catch((err) => {
            console.error('OffroadEmissionsChart: Error in buildChart:', err);
            setError(`Failed to load chart: ${err.message || 'Unknown error'}`);
            creatingRef.current = false;
            setLoading(false);
          });
        } else {
          console.warn('OffroadEmissionsChart: Canvas still not available after delay');
          setError('Chart canvas not available');
          setLoading(false);
        }
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        try {
          if (chartRef.current) chartRef.current.destroy();
          if (canvasNode) {
            const existing = Chart.getChart(canvasNode as unknown as HTMLCanvasElement);
            if (existing) existing.destroy();
          }
        } catch (err) {
          console.warn('OffroadEmissionsChart: Error cleaning up chart:', err);
        }
      };
    }

    // Canvas is available, build chart immediately
    buildChart().catch((err) => {
      console.error('OffroadEmissionsChart: Error in buildChart:', err);
      setError(`Failed to load chart: ${err.message || 'Unknown error'}`);
      creatingRef.current = false;
      setLoading(false);
    });
    
    const canvasNode = canvasRef.current;
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasNode) {
          const existing = Chart.getChart(canvasNode as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch (err) {
        console.warn('OffroadEmissionsChart: Error cleaning up chart:', err);
      }
    };
  }, [buildChart, location]);

  return (
    <div className="chart-container" style={{ height: '400px', position: 'relative' }}>
      {loading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1 }}>
          Loading offroad emissions data...
        </div>
      )}
      {error && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1, color: '#ef4444' }}>
          {error}
        </div>
      )}
      <canvas ref={canvasRef} style={{ height: '400px', width: '100%', display: loading || error ? 'none' : 'block' }} />
    </div>
  );
};

export default OffroadEmissionsChart;

